import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { hasCoPowers } from '@cosvision/shared';
import { useAuthStore } from '../../store/auth-store.js';
import { useSidebarStore } from '../../store/sidebar-store.js';
import { useInactivityGuard } from '../../hooks/useInactivityGuard.js';
import { useNativePush } from '../../hooks/useNativePush.js';
import { useOtaUpdate } from '../../hooks/useOtaUpdate.js';
import { useIsMobile } from '../../hooks/useMediaQuery.js';
import { Sidebar } from './Sidebar.js';
import { Header } from './Header.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { AppLockScreen } from '../auth/AppLockScreen.js';
import { MobileShell } from './MobileShell.js';
import { OtaUpdateBanner } from './OtaUpdateBanner.js';

// The drawer pulls in Recharts (~118 kB gz) for chart rendering — lazy-load
// so the operator's bundle never touches it.
const AICopilotDrawer = lazy(() =>
  import('../ai/AICopilotDrawer.js').then((m) => ({ default: m.AICopilotDrawer })),
);

// Task overlay host (popup + edit drawer) is shared across every page that
// can deep-link to a task via `?task=<id>`. Lazy so the operator (who never
// touches tasks) doesn't pay for it.
const TaskOverlayHost = lazy(() =>
  import('../tasks/TaskOverlayHost.js').then((m) => ({ default: m.TaskOverlayHost })),
);

export function AppShell({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const isMobileOpen = useSidebarStore((s) => s.isMobileOpen);
  const closeMobile = useSidebarStore((s) => s.closeMobile);

  // 30-min inactivity: logs out on web, arms the app-lock overlay on native
  // (CLAUDE.md session rules; android-app-plan §4.3 / M1.7).
  useInactivityGuard();

  // Native push: request permission + register the FCM token once signed in,
  // and route notification taps to their deep link (android-app-plan §4.4 /
  // M4.3). No-op on web.
  useNativePush();

  // OTA: check for a newer web bundle, download it, and prompt to restart
  // (android-app-plan §4.7 / M5). No-op on web.
  const ota = useOtaUpdate();
  const otaBanner = ota.ready ? (
    <OtaUpdateBanner applying={ota.applying} onApply={ota.apply} />
  ) : null;

  // Below `lg` the app renders the mobile bottom-tab shell; the desktop
  // sidebar tree below is byte-for-byte unchanged at `lg+`
  // (android-app-plan §6.1 / M2.1).
  const isMobile = useIsMobile();

  // While the mobile nav drawer is open: lock body scroll behind it and let
  // Escape dismiss it. Both effects no-op when the drawer is closed.
  useEffect(() => {
    if (!isMobileOpen) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeMobile();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobileOpen, closeMobile]);

  if (isMobile) {
    return (
      <>
        {otaBanner}
        <MobileShell>{children}</MobileShell>
      </>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {otaBanner}
      {/* Keyboard skip-link — visually hidden until focused, then it surfaces
          at the top-left so a screen reader / Tab user can jump straight
          to the main content past the sidebar nav. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-bn-800 focus:px-3 focus:py-1.5 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to main content
      </a>
      {/* Dim backdrop behind the mobile nav drawer — tap to dismiss. Hidden
          at md+ where the sidebar is a persistent in-flow rail. */}
      {isMobileOpen ? (
        <div
          className="print-hide fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          aria-hidden="true"
          onClick={closeMobile}
        />
      ) : null}
      <div className="print-hide">
        <Sidebar />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="print-hide">
          <Header />
        </div>
        <main
          id="main"
          // `relative` makes <main> the positioning context for any
          // `position: absolute` descendant — e.g. `sr-only` spans inside
          // form labels. Without it those absolutes escape to the initial
          // containing block and inflate `documentElement.scrollHeight`,
          // which renders as a slate-50 band below the bounded AppShell on
          // tall fund pages.
          className="relative min-w-0 flex-1 overflow-y-auto p-4 sm:p-6"
          tabIndex={-1}
        >
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </div>
      {hasCoPowers(user) ? (
        <Suspense fallback={null}>
          <div className="print-hide">
            <AICopilotDrawer />
          </div>
        </Suspense>
      ) : null}
      {role === 'CO' || role === 'OFFICER' ? (
        <Suspense fallback={null}>
          <TaskOverlayHost />
        </Suspense>
      ) : null}
      {/* Native-only inactivity lock — renders null unless armed. */}
      <AppLockScreen />
    </div>
  );
}
